import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';

// Define the Team Member type to match our database structure
interface TeamMember {
  id: string;
  name: string;
  role: string;
  photo: string;
  bio: string;
  display_order: number;
  is_active: boolean;
}

const timeline = [
  { year: '2010', event: 'Healing Lotus founded with a vision for holistic wellness in the heart of Dallas.' },
  { year: '2014', event: 'Expanded our facilities to offer advanced facial and body treatments.' },
  { year: '2018', event: 'Awarded Best Luxury Spa in Dallas-Fort Worth metro area.' },
  { year: '2023', event: 'Launched exclusive membership program and wellness workshops.' },
  { year: '2025', event: 'Celebrating 15 years with our renovated space and new treatment offerings.' },
];

function About() {
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation(['about', 'common']);

  useEffect(() => {
    // Fetch team data from Supabase
    const fetchTeam = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const { data, error } = await supabase
          .from('team')
          .select('*')
          .eq('is_active', true)
          .order('display_order', { ascending: true });
          
        if (error) {
          throw error;
        }
        
        setTeam(data || []);
      } catch (err: any) {
        console.error('Error fetching team data:', err);
        setError('Failed to load team data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchTeam();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8F6F2] via-[#E8F5F2] to-[#F9E9E9] font-sans">
      <SEO
        title="About Healing Lotus Spa — Dallas's Luxury Wellness Sanctuary"
        description="Learn about Healing Lotus Skin Spa in Dallas, TX. Our story, our therapists, and our philosophy of grounded, science-based luxury skincare and massage."
        path="/about"
      />
      {/* Hero Section */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-[#232347]/60 z-10"></div>
        <div className="relative z-20 text-center px-4">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-4 font-serif">{t('about:hero.title')}</h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto">{t('about:hero.subtitle')}</p>
        </div>
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.pexels.com/photos/3757952/pexels-photo-3757952.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1"
            alt="Serene spa treatment room with minimalist design"
            className="w-full h-full object-cover"
          />
        </div>
      </section>

      <div className="container mx-auto py-16 px-4 md:px-6">
        {/* The Beginning Section */}
        <section className="mb-20">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="w-full lg:w-1/2 rounded-xl overflow-hidden shadow-xl">
              <img
                src="https://images.pexels.com/photos/3865675/pexels-photo-3865675.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt="Serene spa treatment room with stones and candles"
                className="w-full h-[400px] object-cover"
              />
            </div>
            <div className="w-full lg:w-1/2">
              <h2 className="text-3xl md:text-4xl font-serif font-semibold text-[#232347] mb-6">{t('about:mission.title')}</h2>
              <p className="text-[#232347]/80 mb-6 leading-relaxed">
                {t('about:mission.paragraph1')}
              </p>
              <p className="text-[#232347]/80 mb-6 leading-relaxed">
                {t('about:mission.paragraph2')}
              </p>
              <p className="text-[#232347]/80 leading-relaxed italic">
                {t('about:mission.paragraph3')}
              </p>
            </div>
          </div>
        </section>

        {/* Our Philosophy Section */}
        <section className="mb-20 bg-gradient-to-br from-[#F8F6F2] to-[#E8F5F2] -mx-4 md:-mx-6 px-4 md:px-6 py-16 rounded-2xl">
          <h2 className="text-3xl md:text-4xl font-serif font-semibold text-[#232347] mb-4 text-center">{t('about:healingPhilosophy.title')}</h2>
          <p className="text-xl text-[#232347] mb-6 text-center max-w-3xl mx-auto leading-relaxed italic">
            {t('about:healingPhilosophy.subtitle')}
          </p>
          <p className="text-[#232347]/80 mb-4 text-center max-w-3xl mx-auto leading-relaxed">
            {t('about:healingPhilosophy.description')}
          </p>
          <p className="text-[#232347]/80 mb-12 text-center max-w-3xl mx-auto leading-relaxed font-medium">
            {t('about:healingPhilosophy.approach')}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Sound Energy */}
            <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-[#B2D8C5]/20 rounded-full flex items-center justify-center mb-6 mx-auto">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-[#B2D8C5]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                </svg>
              </div>
              <h3 className="text-xl font-serif font-semibold text-[#232347] mb-2 text-center">{t('about:healingPhilosophy.sound.title')}</h3>
              <p className="text-[#B2D8C5] font-medium mb-4 text-center text-sm">{t('about:healingPhilosophy.sound.subtitle')}</p>
              <p className="text-[#232347]/70 text-sm text-center leading-relaxed">
                {t('about:healingPhilosophy.sound.description')}
              </p>
            </div>

            {/* Therapeutic Skincare */}
            <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-[#F7CAC9]/20 rounded-full flex items-center justify-center mb-6 mx-auto">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-[#F7CAC9]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-serif font-semibold text-[#232347] mb-2 text-center">{t('about:healingPhilosophy.soul.title')}</h3>
              <p className="text-[#F7CAC9] font-medium mb-4 text-center text-sm">{t('about:healingPhilosophy.soul.subtitle')}</p>
              <p className="text-[#232347]/70 text-sm text-center leading-relaxed">
                {t('about:healingPhilosophy.soul.description')}
              </p>
            </div>

            {/* Advanced Management */}
            <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-[#B2D8C5]/20 rounded-full flex items-center justify-center mb-6 mx-auto">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-[#B2D8C5]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
              </div>
              <h3 className="text-xl font-serif font-semibold text-[#232347] mb-2 text-center">{t('about:healingPhilosophy.skin.title')}</h3>
              <p className="text-[#B2D8C5] font-medium mb-4 text-center text-sm">{t('about:healingPhilosophy.skin.subtitle')}</p>
              <p className="text-[#232347]/70 text-sm text-center leading-relaxed">
                {t('about:healingPhilosophy.skin.description')}
              </p>
            </div>
          </div>

          <p className="text-[#232347]/80 mt-12 text-center max-w-3xl mx-auto leading-relaxed italic">
            {t('about:healingPhilosophy.closing')}
          </p>
        </section>

        {/* The Space Section */}
        <section className="mb-20">
          <div className="flex flex-col lg:flex-row-reverse items-center gap-12">
            <div className="w-full lg:w-1/2 rounded-xl overflow-hidden shadow-xl">
              <img
                src="https://images.pexels.com/photos/6621374/pexels-photo-6621374.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt="Modern minimalist spa interior"
                className="w-full h-[400px] object-cover"
              />
            </div>
            <div className="w-full lg:w-1/2">
              <h2 className="text-3xl md:text-4xl font-serif font-semibold text-[#232347] mb-6">{t('about:space.title')}</h2>
              <p className="text-[#232347]/80 mb-6 leading-relaxed">
                {t('about:space.paragraph1')}
              </p>
              <p className="text-[#232347]/80 mb-6 leading-relaxed">
                {t('about:space.paragraph2')}
              </p>
              <p className="text-[#232347]/80 leading-relaxed italic">
                {t('about:space.closing')}
              </p>
            </div>
          </div>
        </section>

        {/* Meet Our Team Section */}
        <section className="mb-20">
          <h2 className="text-3xl md:text-4xl font-serif font-semibold text-[#232347] mb-8 text-center">{t('about:team.title')}</h2>
          <p className="text-[#232347]/80 mb-12 leading-relaxed text-center max-w-3xl mx-auto">
            {t('about:team.description')}
          </p>
          
          {error && (
            <div className="text-red-500 text-center mb-8 p-4 bg-red-50 rounded-lg border border-red-100">
              {error}
            </div>
          )}
          
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="inline-block w-12 h-12 border-4 border-[#B2D8C5] border-t-[#232347] rounded-full animate-spin"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10 max-w-6xl mx-auto">
              {team.map((member) => (
                <div key={member.id} className="bg-white/80 rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300">
                  <div className="h-80 overflow-hidden relative">
                    {member.photo ? (
                      <img
                        src={member.photo}
                        alt={member.name}
                        className="w-full h-80 object-cover object-top transition-transform duration-300 hover:scale-105"
                      />
                    ) : (
                      <div className="flex items-center justify-center h-full bg-gray-100">
                        <div className="text-gray-400">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-20 w-20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-[#232347] mb-1 font-serif">{member.name}</h3>
                    <p className="text-[#B2D8C5] font-medium mb-4">{member.role}</p>
                    <p className="text-[#232347]/70 text-sm">{member.bio}</p>
                  </div>
                </div>
              ))}
              
              {team.length === 0 && !loading && !error && (
                <div className="col-span-full text-center py-12 text-gray-500">
                  No team members found. Check back soon!
                </div>
              )}
            </div>
          )}
        </section>

        {/* Our Journey Section */}
        <section className="mb-20">
          <h2 className="text-3xl md:text-4xl font-serif font-semibold text-[#232347] mb-8 text-center">{t('about:journey.title')}</h2>
          <p className="text-[#232347]/80 mb-12 leading-relaxed text-center max-w-3xl mx-auto">
            {t('about:journey.description')}
          </p>
          
          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-[#B2D8C5] hidden md:block"></div>
            
            {/* Timeline Events */}
            <div className="space-y-12">
              {timeline.map((item, idx) => (
                <div key={idx} className={`flex flex-col md:flex-row ${idx % 2 === 0 ? 'md:flex-row-reverse' : ''} items-center`}>
                  <div className={`md:w-1/2 flex ${idx % 2 === 0 ? 'justify-start' : 'justify-end'} relative`}>
                    <div className="bg-white rounded-xl shadow-md p-6 max-w-md z-10 mb-4 md:mb-0 w-full md:w-auto">
                      <h3 className="text-xl font-semibold text-[#B2D8C5] mb-2">{item.year}</h3>
                      <p className="text-[#232347]/80">{item.event}</p>
                    </div>
                  </div>
                  
                  <div className="md:w-0 relative flex justify-center">
                    <div className="absolute z-20 w-10 h-10 rounded-full bg-[#F7CAC9] border-4 border-white flex items-center justify-center">
                      <div className="w-2 h-2 rounded-full bg-white"></div>
                    </div>
                  </div>
                  
                  <div className="md:w-1/2"></div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Location Section */}
        <section className="mb-20">
          <h2 className="text-3xl md:text-4xl font-serif font-semibold text-[#232347] mb-8 text-center">{t('about:location.title')}</h2>
          <div className="flex flex-col lg:flex-row gap-12 items-center">
            <div className="w-full lg:w-1/2">
              <div className="bg-white/80 rounded-xl shadow-md p-8">
                <h3 className="text-2xl font-serif font-semibold text-[#232347] mb-4">{t('about:location.name')}</h3>
                <p className="text-[#232347]/80 mb-2">{t('about:location.address1')}</p>
                <p className="text-[#232347]/80 mb-6">{t('about:location.address2')}</p>
                
                <h4 className="text-lg font-semibold text-[#232347] mb-2">{t('about:location.hours')}</h4>
                <div className="grid grid-cols-2 gap-y-2 text-[#232347]/80 mb-6">
                  <span>{t('about:location.schedule.weekdays')}</span>
                  <span>{t('about:location.schedule.weekdaysHours')}</span>
                  <span>{t('about:location.schedule.saturday')}</span>
                  <span>{t('about:location.schedule.saturdayHours')}</span>
                  <span>{t('about:location.schedule.sunday')}</span>
                  <span>{t('about:location.schedule.sundayHours')}</span>
                </div>
                
                <h4 className="text-lg font-semibold text-[#232347] mb-2">{t('about:location.contact')}</h4>
                <p className="text-[#232347]/80 mb-1">{t('about:location.phone')}</p>
                <p className="text-[#232347]/80 mb-4">{t('about:location.email')}</p>
                
                <div className="flex gap-4 mt-6">
                  <a href="https://maps.google.com/?q=4222+Trinity+Mills+Rd+Suite+120,+Dallas,+TX+75024" target="_blank" rel="noopener noreferrer" className="bg-[#B2D8C5] hover:bg-[#88bfa3] text-white px-5 py-2 rounded-md text-sm font-medium transition-colors inline-flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                    </svg>
                    {t('common:buttons.getDirections')}
                  </a>
                  <button className="bg-[#F7CAC9] hover:bg-[#e6b9b8] text-white px-5 py-2 rounded-md text-sm font-medium transition-colors inline-flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                    </svg>
                    {t('common:buttons.callNow')}
                  </button>
                </div>
              </div>
            </div>
            
            <div className="w-full lg:w-1/2 rounded-xl overflow-hidden shadow-xl h-[400px]">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3343.5084836697287!2d-96.83058892392145!3d33.05531387357048!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x864c3d4f2b4f3f0f%3A0x1234567890abcdef!2s4222%20Trinity%20Mills%20Rd%20Suite%20120%2C%20Dallas%2C%20TX%2075024!5e0!3m2!1sen!2sus!4v1715649481489!5m2!1sen!2sus"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Healing Lotus Spa Location"
              ></iframe>
            </div>
          </div>
        </section>
      </div>

      {/* Our Purpose - Call to Action */}
      <section className="bg-[#232347] text-white py-16 px-4">
        <div className="container mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-serif font-semibold mb-6">{t('about:cta.title')}</h2>
          <p className="max-w-2xl mx-auto mb-6 text-white/90 text-lg leading-relaxed">{t('about:cta.description')}</p>
          <p className="max-w-2xl mx-auto mb-8 text-white/80 leading-relaxed italic">{t('about:cta.closing')}</p>
          <Link to="/booking" className="inline-block bg-[#B2D8C5] hover:bg-[#88bfa3] text-white px-8 py-3 rounded-md font-semibold text-lg transition-colors">
            {t('about:cta.button')}
          </Link>
        </div>
      </section>
    </div>
  );
}

export default About; 